import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ChevronRight, Download, RefreshCw, X } from 'lucide-react';
import { api } from '../api.js';

export const op = '/operations';
export const currency = new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 });
export const number = new Intl.NumberFormat('vi-VN');
const VN = 'Asia/Ho_Chi_Minh';
const dateTimeFormat = new Intl.DateTimeFormat('vi-VN', { dateStyle: 'short', timeStyle: 'short', timeZone: VN });
const timeFormat = new Intl.DateTimeFormat('vi-VN', { hour: '2-digit', minute: '2-digit', timeZone: VN });

/** Số tiền VND dùng dấu chấm ngăn hàng nghìn: 35000 → "35.000". */
export function formatVnd(value) {
  if (value == null || value === '' || value === '-') return value === '-' ? '-' : '';
  const amount = Math.round(Number(value));
  if (Number.isNaN(amount)) return '';
  return `${amount < 0 ? '-' : ''}${String(Math.abs(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
}
export const money = (value) => (value == null || value === '' ? '' : `${formatVnd(value)} đ`);

/** Ô nhập tiền: hiển thị 35.000 trong lúc gõ, trả về chuỗi số thô ("35000") cho onChange. */
export function MoneyInput({ value, onChange, allowNegative = false, ...props }) {
  const change = (event) => {
    const text = event.target.value;
    const digits = text.replace(/[^0-9]/g, '').replace(/^0+(?=\d)/, '');
    onChange(`${allowNegative && text.trim().startsWith('-') ? '-' : ''}${digits}`);
  };
  return <input type="text" inputMode={allowNegative ? 'text' : 'numeric'} autoComplete="off" value={formatVnd(value)} onChange={change} {...props} />;
}

export function MoneyField({ label, value, onChange, wide, hint, error, allowNegative, ...props }) {
  return <label className={wide ? 'wide' : ''}>{label}
    <MoneyInput value={value} onChange={onChange} allowNegative={allowNegative} aria-invalid={Boolean(error)} {...props} />
    {error ? <small className="field-error">{error}</small> : hint ? <small className="field-hint">{hint}</small> : null}
  </label>;
}
export const dateTime = (value) => (value ? dateTimeFormat.format(new Date(value)) : '—');
export const timeOnly = (value) => (value ? timeFormat.format(new Date(value)) : '—');
export const dayLabel = (value) => (value ? String(value).slice(0, 10).split('-').reverse().join('/') : '—');
/** Giá trị cho input datetime-local / date theo giờ Việt Nam. */
export const localInput = (value) => (value ? new Date(value).toLocaleString('sv-SE', { timeZone: VN }).slice(0, 16).replace(' ', 'T') : '');
export const fromLocalInput = (value) => new Date(`${value}:00+07:00`).toISOString();
export const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: VN });
/** Ngày vận hành như bảng booking cũ: trước 03:00 sáng vẫn tính là ngày hôm trước. */
export const operationalDate = () => new Date(Date.now() - 3 * 60 * 60 * 1000).toLocaleDateString('sv-SE', { timeZone: VN });

export const statusNames = {
  pending: 'Chờ xác nhận', confirmed: 'Đã xác nhận', checked_in: 'Đã đến', completed: 'Hoàn tất', cancelled: 'Đã hủy', no_show: 'Không đến',
  active: 'Hoạt động', inactive: 'Ngừng', maintenance: 'Bảo trì', disabled: 'Vô hiệu',
  open: 'Đang mở', closed: 'Đã đóng', approved: 'Đã duyệt', not_opened: 'Chưa mở',
  in_stock: 'Còn hàng', low_stock: 'Sắp hết', out_of_stock: 'Hết hàng',
  available: 'Trống', reserved: 'Đã đặt', playing: 'Đang chơi',
  upcoming: 'Sắp tới', ongoing: 'Đang diễn ra', overdue: 'Quá giờ',
};
export const paymentNames = { cash: 'TM', transfer: 'CK', other: 'Khác' };
export const pointTypeNames = { earn: 'Tích điểm', earn_reversal: 'Hoàn điểm do hủy hóa đơn', adjustment: 'Điều chỉnh', redemption: 'Đổi quà', redemption_reversal: 'Hoàn do hủy đổi' };
export const stockTypeNames = { import: 'Nhập kho', adjustment: 'Điều chỉnh', redemption: 'Đổi quà', redemption_reversal: 'Hoàn do hủy đổi' };

export function can(auth, permission) {
  return auth.user.role_key === 'super_admin' || auth.user.permissions?.includes(permission);
}

export function queryString(params) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== '' && value != null) search.set(key, value);
  const text = search.toString();
  return text ? `?${text}` : '';
}

export function Header({ eyebrow, title, description, children }) {
  return <header className="page-header"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1><p>{description}</p></div><div className="page-actions">{children}</div></header>;
}

export function Button({ children, tone = 'subtle', ...props }) {
  return <button className={`btn ${tone}`} {...props}>{children}</button>;
}

export function Modal({ title, onClose, children, wide = false }) {
  return <div className="modal-backdrop"><section className={`modal ops-modal ${wide ? 'wide-modal' : ''}`}>
    <button type="button" className="icon-btn modal-close" onClick={onClose} title="Đóng"><X /></button>
    <p className="eyebrow">ONEPLAY OPERATIONS</p><h2>{title}</h2>{children}
  </section></div>;
}

export function Select({ label, value, onChange, options, placeholder = 'Chọn...', required = false, disabled = false }) {
  return <label>{label}<select value={value ?? ''} onChange={(event) => onChange(event.target.value)} required={required} disabled={disabled}>
    {placeholder !== null && <option value="">{placeholder}</option>}
    {options.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
  </select></label>;
}

export function Field({ label, type = 'text', value, onChange, wide, hint, error, ...props }) {
  return <label className={wide ? 'wide' : ''}>{label}
    <input type={type} value={value ?? ''} onChange={(event) => onChange(event.target.value)} aria-invalid={Boolean(error)} {...props} />
    {error ? <small className="field-error">{error}</small> : hint ? <small className="field-hint">{hint}</small> : null}
  </label>;
}

export function Status({ value, label }) {
  return <span className={`status-pill ${value}`}>{label || statusNames[value] || value || '—'}</span>;
}

export function Tabs({ tabs, value, onChange }) {
  return <div className="ops-tabs" role="tablist">{tabs.map((tab) => <button key={tab.id} type="button" role="tab" aria-selected={value === tab.id} className={value === tab.id ? 'active' : ''} onClick={() => onChange(tab.id)}>{tab.label}</button>)}</div>;
}

export function FilterBar({ children }) {
  return <section className="card filter-bar">{children}</section>;
}

/** Bảng có đủ ba trạng thái: đang tải, lỗi (kèm thử lại) và rỗng. */
export function Table({ columns, rows, actions, loading = false, error = null, onRetry, empty = 'Chưa có dữ liệu', rowKey, rowClass }) {
  if (error) return <div className="ops-empty error"><AlertTriangle size={20} /><span>{error}</span>{onRetry && <Button onClick={onRetry}><RefreshCw size={15} /> Thử lại</Button>}</div>;
  if (loading && !rows?.length) return <div className="ops-empty"><RefreshCw className="spin" size={18} /> Đang tải dữ liệu...</div>;
  if (!rows?.length) return <div className="ops-empty">{empty}</div>;
  return <div className={`table-wrap ${loading ? 'is-loading' : ''}`}><table><thead><tr>{columns.map((c) => <th key={c.key} className={c.align === 'right' ? 'num' : ''}>{c.label}</th>)}{actions && <th>Thao tác</th>}</tr></thead>
    <tbody>{rows.map((row, index) => <tr key={rowKey ? rowKey(row) : (row.id ?? index)} className={rowClass ? rowClass(row) : ''}>
      {columns.map((c) => <td key={c.key} className={c.align === 'right' ? 'num' : ''}>{c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}</td>)}
      {actions && <td><div className="row-actions text">{actions(row)}</div></td>}
    </tr>)}</tbody></table></div>;
}

export function Pagination({ pagination, onPage }) {
  if (!pagination || pagination.total === 0) return null;
  const { page, total_pages: totalPages, total, page_size: pageSize } = pagination;
  const first = (page - 1) * pageSize + 1;
  return <div className="pagination"><span>{number.format(first)}–{number.format(Math.min(total, page * pageSize))} / {number.format(total)} dòng</span>
    <div><button type="button" className="table-action" disabled={page <= 1} onClick={() => onPage(page - 1)} title="Trang trước"><ChevronLeft size={15} /></button>
      <strong>{page} / {totalPages}</strong>
      <button type="button" className="table-action" disabled={page >= totalPages} onClick={() => onPage(page + 1)} title="Trang sau"><ChevronRight size={15} /></button></div>
  </div>;
}

export function ExportButton({ path, filename, onError, label = 'Xuất CSV' }) {
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { await api.download(path, filename); } catch (error) { onError(error); } finally { setBusy(false); } };
  return <Button onClick={run} disabled={busy}>{busy ? <RefreshCw className="spin" size={15} /> : <Download size={15} />} {label}</Button>;
}

export function useOpsContext() {
  const { auth, setNotice } = useOutletContext();
  const [stores, setStores] = useState([]);
  const fail = useCallback((error) => setNotice({ type: 'error', message: error.message }), [setNotice]);
  const success = useCallback((message) => setNotice({ type: 'success', message }), [setNotice]);
  useEffect(() => { api.get(`${op}/context/stores`).then((result) => setStores(result.data)).catch(fail); }, [fail]);
  return { auth, stores, fail, success };
}

/** Tải lại khi có sự kiện Socket.IO hoặc khi socket kết nối lại; gom các sự kiện dồn dập thành một lần gọi API. */
export function useRealtime(reload) {
  useEffect(() => {
    let timer = null;
    const refresh = () => { clearTimeout(timer); timer = setTimeout(reload, 250); };
    window.addEventListener('oneplay:realtime', refresh);
    return () => { clearTimeout(timer); window.removeEventListener('oneplay:realtime', refresh); };
  }, [reload]);
}

/** Đọc một API GET, tự đồng bộ realtime. `path = null` thì chưa gọi. */
export function useQuery(path, { realtime = true } = {}) {
  const [state, setState] = useState({ loading: Boolean(path), data: null, error: null });
  const latest = useRef(0);
  const reload = useCallback(() => {
    if (!path) { setState({ loading: false, data: null, error: null }); return Promise.resolve(); }
    const ticket = ++latest.current;
    setState((current) => ({ ...current, loading: true, error: null }));
    return api.get(path)
      .then((data) => { if (ticket === latest.current) setState({ loading: false, data, error: null }); })
      .catch((error) => { if (ticket === latest.current) setState((current) => ({ loading: false, data: current.data, error: error.message })); });
  }, [path]);
  useEffect(() => { reload(); }, [reload]);
  useRealtime(realtime ? reload : noop);
  return { ...state, rows: state.data?.data ?? null, pagination: state.data?.pagination ?? null, reload };
}
function noop() {}

/**
 * Hộp thoại nhập liệu/xác nhận thay cho window.prompt/confirm.
 * `const [ask, dialog] = useAsk()`; `const values = await ask({ title, fields })` trả về null nếu người dùng hủy.
 */
export function useAsk() {
  const [request, setRequest] = useState(null);
  const ask = useCallback((config) => new Promise((resolve) => setRequest({ config, resolve })), []);
  const done = (values) => { request.resolve(values); setRequest(null); };
  return [ask, request ? <AskDialog key={request.config.title} config={request.config} onDone={done} /> : null];
}

function AskDialog({ config, onDone }) {
  const fields = config.fields || [];
  const [values, setValues] = useState(() => Object.fromEntries(fields.map((field) => [field.key, field.defaultValue ?? ''])));
  const submit = (event) => { event.preventDefault(); onDone(values); };
  return <div className="modal-backdrop"><form className="modal confirm-modal" onSubmit={submit}>
    <button type="button" className="icon-btn modal-close" onClick={() => onDone(null)} title="Đóng"><X /></button>
    {config.danger && <div className="danger-icon"><AlertTriangle /></div>}
    <p className="eyebrow">XÁC NHẬN</p><h2>{config.title}</h2>
    {config.message && <p>{config.message}</p>}
    <div className="ask-fields">{fields.map((field, index) => field.options
      ? <Select key={field.key} label={field.label} value={values[field.key]} onChange={(value) => setValues({ ...values, [field.key]: value })} options={field.options} required={field.required} placeholder={field.placeholder} />
      : field.type === 'money' ? <MoneyField key={field.key} label={field.label} value={values[field.key]} onChange={(value) => setValues({ ...values, [field.key]: value })} required={field.required} hint={field.hint} placeholder={field.placeholder} autoFocus={index === 0} />
      : <Field key={field.key} label={field.label} type={field.type || 'text'} value={values[field.key]} onChange={(value) => setValues({ ...values, [field.key]: value })} required={field.required} min={field.min} minLength={field.minLength} step={field.step} hint={field.hint} placeholder={field.placeholder} autoFocus={index === 0} />)}</div>
    <div className="form-actions"><Button type="button" onClick={() => onDone(null)}>Hủy</Button><Button tone={config.danger ? 'danger' : 'primary'}>{config.confirmLabel || 'Xác nhận'}</Button></div>
  </form></div>;
}
